import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const POLL_MS = 120000;
const STYLES = {
  HEALTHY: { dot: "bg-emerald-500", label: "Healthy" },
  DEGRADED: { dot: "bg-amber-500", label: "Degraded" },
  OUTAGE: { dot: "bg-red-600", label: "Outage" },
  UNKNOWN: { dot: "bg-[#A3A3A3]", label: "Status unavailable" },
};

/**
 * Header status dot. Reads the CACHED summary (`/health/summary` never
 * runs component checks inline), polls every 2 minutes with a short
 * timeout, and degrades to "Status unavailable" on any error — it can
 * never block or freeze the shell.
 */
export default function HealthIndicator() {
  const { isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const [overall, setOverall] = useState(null);
  const admin = isPlatformAdmin();

  useEffect(() => {
    let alive = true;
    let ctrl = null;
    const load = async () => {
      ctrl?.abort();
      ctrl = new AbortController();
      try {
        const r = await api.get("/health/summary", { signal: ctrl.signal, timeout: 8000 });
        if (alive) setOverall(r.data?.overall || "UNKNOWN");
      } catch (e) {
        if (alive && e?.code !== "ERR_CANCELED") setOverall("UNKNOWN");
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); ctrl?.abort(); };
  }, []);

  if (overall === null) return null;
  const s = STYLES[overall] || STYLES.UNKNOWN;
  const content = (
    <>
      <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} aria-hidden="true" />
      <span className="hidden sm:inline">{s.label}</span>
    </>
  );
  if (!admin) {
    return (
      <span className="flex items-center gap-1.5 px-2 h-8 text-xs text-[#525252]" title={`Pulse status: ${s.label}`}
        data-testid="health-indicator">{content}</span>
    );
  }
  return (
    <button
      onClick={() => navigate("/admin?tab=health")}
      className="flex items-center gap-1.5 px-2 h-8 text-xs text-[#525252] hover:bg-[#F5F5F5] transition-colors"
      title={`Pulse status: ${s.label} — open System Health`}
      aria-label={`Pulse status: ${s.label}. Open System Health`}
      data-testid="health-indicator"
    >{content}</button>
  );
}
